import BasemapGalleryVM from "@arcgis/core/widgets/BasemapGallery/BasemapGalleryViewModel.js";
import { useEffect, useCallback, useState } from "react";
import BasemapItem from "./BasemapItem";
import Collapsible from "../../../genericComponents/Collapsible/Collapsible";

import MountainsIcon from "../../../../assets/layerlist/mountains.svg?react";
import BoatIcon from "../../../../assets/layerlist/boat.svg?react";

import "./LayerlistBackground.css";
import { addThemeLayersToMap } from "../../../../utils/layerlistUtils";
import { getMapView } from "../../../../utils/arcgisUtils";
import Loader from "../../../genericComponents/Loader/Loader";
import type WebMap from "@arcgis/core/WebMap";
import type Basemap from "@arcgis/core/Basemap";

interface LayerlistBackgroundProps {
    basemapGalleryVM: BasemapGalleryVM | null;
    themeMaps: WebMap[];
    refreshThemeMaps: () => void;
    loadBasemapGallery: () => Promise<BasemapGalleryVM | null>;
    onSettingsChanged?: () => void;
}

const LayerlistBackground = (props: LayerlistBackgroundProps) => {
    const [activeBasemapId, setActiveBasemapId] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(true);

    const selectBasemap = useCallback(
        async (basemap: Basemap) => {
            if (props.basemapGalleryVM) {
                // Make sure the basemap is fully loaded before setting it as active
                await basemap.load();
                // Set the active basemap
                props.basemapGalleryVM.activeBasemap = basemap;

                // Update the state after the map has been updated
                setActiveBasemapId(basemap.id ?? null);

                // Auto-save settings after basemap change
                props.onSettingsChanged?.();
            }
        },
        [props.basemapGalleryVM, props.onSettingsChanged]
    );

    const selectThemeMap = useCallback(
        async (themeMap: WebMap) => {
            if (props.basemapGalleryVM) {
                // First set the active basemap
                // Make sure the basemap is fully loaded before adding theme layers
                await themeMap.basemap?.load();

                const mapview = getMapView();
                if (mapview) {
                    if (mapview.spatialReference !== themeMap.basemap?.spatialReference) {
                        if (themeMap.basemap?.spatialReference) {
                            mapview.spatialReference = themeMap.basemap?.spatialReference;
                        }
                    }
                }

                props.basemapGalleryVM.activeBasemap = themeMap.basemap;
                // Now add the theme layers
                await addThemeLayersToMap(themeMap);

                // Update the UI and state after the map has been fully updated
                props.refreshThemeMaps();
                setActiveBasemapId(themeMap.portalItem?.id ?? null);

                // Auto-save settings after theme map change
                props.onSettingsChanged?.();
            }
        },
        [props.basemapGalleryVM, props.refreshThemeMaps, props.onSettingsChanged]
    );

    useEffect(() => {
        if (props.basemapGalleryVM) {
            // @ts-ignore functionality missing from typings
            props.basemapGalleryVM.refresh();

            const updateActiveBasemap = () => {
                const activeBasemap = props.basemapGalleryVM?.activeBasemap;
                setActiveBasemapId(activeBasemap?.id ?? null);
            };

            updateActiveBasemap(); // Set initial active basemap
        }
    }, [props.basemapGalleryVM]);

    useEffect(() => {
        if ((props.basemapGalleryVM?.items.length ?? 0) < 1) {
            props.loadBasemapGallery();
        }
    }, []);

    const retryRender = () => {
        setTimeout(() => {
            if (
                props.basemapGalleryVM?.items.some(
                    (item) =>
                        item.basemap.thumbnailUrl !== null &&
                        item.basemap.thumbnailUrl !== undefined &&
                        item.basemap.thumbnailUrl !== ""
                )
            ) {
                setLoaded(false);
                setTimeout(() => {
                    setLoaded(true);
                }, 10);
            }
        }, 1000);
    };

    return (
        <div className="layerlist-background">
            {!loaded && <Loader inline />}
            {loaded && (
                <Collapsible
                    isOpen={true}
                    setIsOpen={() => {}}
                    title="Bakgrunnskart"
                    icon={<MountainsIcon />}
                    titleClassName="layerlist-background-title"
                    className="layerlist-background-collapsible"
                >
                    <div className="layerlist-background-list">
                        {props.basemapGalleryVM?.items.map((basemap) => {
                            const basemapId = basemap.basemap.id ?? "";
                            if (!basemap.basemap.thumbnailUrl) {
                                retryRender();
                            }
                            return (
                                <BasemapItem
                                    key={basemapId}
                                    id={basemapId}
                                    thumbnailUrl={basemap.basemap.thumbnailUrl ?? ""}
                                    title={basemap.basemap.title ?? ""}
                                    selectBasemap={() => selectBasemap(basemap.basemap)}
                                    isActive={activeBasemapId === basemapId}
                                />
                            );
                        })}
                    </div>
                </Collapsible>
            )}
            <Collapsible
                isOpen={false}
                setIsOpen={() => {}}
                title="Temakart"
                icon={<BoatIcon />}
                titleClassName="layerlist-background-title"
                className="layerlist-background-collapsible"
            >
                <div className="layerlist-background-list">
                    {props.themeMaps.map((themeMap) => {
                        return (
                            <BasemapItem
                                key={themeMap.portalItem?.id}
                                id={themeMap.portalItem?.id ?? ""}
                                thumbnailUrl={themeMap.thumbnailUrl ?? ""}
                                title={themeMap.portalItem?.title ?? ""}
                                selectBasemap={() => {
                                    selectThemeMap(themeMap);
                                }}
                                isActive={activeBasemapId === themeMap.portalItem?.id}
                            />
                        );
                    })}
                </div>
            </Collapsible>
        </div>
    );
};

export default LayerlistBackground;
